import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UnoCardView } from "@/components/UnoCard";
import SbCardView from "@/components/SkipBoCard";
import { STORE } from "@/constants/testIds";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const UNO_PREVIEW = [
  { color: "red", value: "5" },
  { color: "yellow", value: "skip" },
  { color: "wild", value: "wild4" },
];
const SB_PREVIEW = [
  { id: "p1", value: 4 },
  { id: "p2", value: "skipbo" },
  { id: "p3", value: 11 },
];

function BgPreview({ skinId }) {
  const label = { bg_default: "Default", bg_cozy: "Cozy", bg_beach: "Beach", bg_forest: "Forest", bg_sakura: "Sakura", bg_neon: "Neon", bg_vapor: "Vapor", bg_aurora: "Aurora", bg_space: "Space", bg_ocean: "Ocean", bg_volcano: "Volcano", bg_matrix: "Matrix", bg_galaxy: "Galaxy", bg_fireflies: "Fireflies" }[skinId] || skinId;
  return (
    <div className={`bg-${skinId} bg-preview w-full h-40 rounded-xl border border-white/10 overflow-hidden relative`}>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <div className="flex gap-1.5" style={{ zIndex: 1 }}>
          <div style={{ transform: "rotate(-10deg)" }}><UnoCardView card={UNO_PREVIEW[0]} size="sm"/></div>
          <div style={{ transform: "rotate(0deg) translateY(-4px)" }}><UnoCardView card={UNO_PREVIEW[1]} size="sm"/></div>
          <div style={{ transform: "rotate(10deg)" }}><UnoCardView card={UNO_PREVIEW[2]} size="sm"/></div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold" style={{ zIndex: 1, textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{label} theme</div>
      </div>
    </div>
  );
}

const authHeader = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function SkinCard({ skin, onBuy, onEquip, coins, category }) {
  return (
    <motion.div whileHover={{ y: -4 }}
      className={`relative rounded-2xl bg-[#161324] border p-4 sm:p-5 flex flex-col overflow-hidden ${skin.active ? "border-[color:var(--uno-yellow)]" : "border-white/5"}`}>
      {skin.badge && (
        <div className={`absolute top-3 -right-8 rotate-45 text-black text-[10px] uppercase tracking-widest font-black px-8 py-0.5 shadow-lg z-10 ${
          skin.badge === "LEGEND"
            ? "bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FF3D00] text-black ring-1 ring-yellow-200"
            : "bg-gradient-to-r from-[color:var(--uno-yellow)] to-[color:var(--uno-red)]"
        }`}>
          {skin.badge}
        </div>
      )}
      {category === "background" ? (
        <div className="mb-4"><BgPreview skinId={skin.id}/></div>
      ) : (
        <div className={`rounded-xl bg-black/40 p-4 sm:p-6 flex justify-center gap-2 mb-4 min-h-[150px] items-center`}>
          {category === "uno"
            ? UNO_PREVIEW.map((c, i) => (
                <div key={i} style={{ transform: `rotate(${(i-1)*8}deg)` }}>
                  <UnoCardView card={c} size="sm" skin={skin.id}/>
                </div>
              ))
            : SB_PREVIEW.map((c, i) => (
                <div key={i} style={{ transform: `rotate(${(i-1)*6}deg) translateY(${Math.abs(i-1)*4}px)` }}>
                  <SbCardView card={c} size="sm" skin={skin.id}/>
                </div>
              ))}
        </div>
      )}
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="font-display text-lg sm:text-xl">{skin.name}</div>
          <div className="text-xs text-white/50 mt-0.5">{skin.tagline}</div>
        </div>
        {skin.active && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--uno-yellow)] text-black font-bold">Equipped</span>}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span>🪙</span>
          <span className="font-display text-base sm:text-lg">{skin.price === 0 ? "Free" : skin.price}</span>
        </div>
        {skin.owned ? (
          skin.active ? (
            <button disabled className="px-4 py-1.5 rounded-full bg-white/10 text-white/50 text-sm">Equipped</button>
          ) : (
            <button data-testid={`${STORE.equipBtnPrefix}${skin.id}`} onClick={() => onEquip(skin.id)}
              className="px-4 py-1.5 rounded-full bg-white text-black text-sm font-semibold">Equip</button>
          )
        ) : (
          <button data-testid={`${STORE.buyBtnPrefix}${skin.id}`} onClick={() => onBuy(skin.id)}
            disabled={coins < skin.price}
            className="px-4 py-1.5 rounded-full bg-[color:var(--uno-red)] text-white text-sm font-semibold disabled:opacity-40">
            {coins < skin.price ? "Not enough" : "Unlock"}
          </button>
        )}
      </div>
    </motion.div>
  );
}

function CoinBundle({ bundle, onBuy, onPayPal, paypalReady, currency }) {
  const displayPrice = currency === "EUR" ? `€${bundle.eur || bundle.price_str.replace("$","")}` : bundle.price_str;
  return (
    <motion.div whileHover={{ y: -4 }} className="relative rounded-2xl bg-[#161324] border border-white/5 p-6 flex flex-col items-center">
      {bundle.badge && (
        <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--uno-yellow)] text-black font-bold">{bundle.badge}</span>
      )}
      <div className="text-6xl mb-2">🪙</div>
      <div className="font-display text-3xl" style={{color:"var(--uno-yellow)"}}>{bundle.coins.toLocaleString()}</div>
      <div className="text-xs uppercase tracking-widest text-white/50 mt-1">coins</div>
      <button
        data-testid={`${STORE.bundleBuyPrefix}${bundle.lookup_key}`}
        onClick={() => onBuy(bundle.lookup_key)}
        className="mt-5 w-full px-6 py-2.5 rounded-full bg-[color:var(--uno-green)] text-black font-semibold text-sm hover:brightness-110"
      >
        Card · {displayPrice}
      </button>
      {paypalReady && (
        <div className="mt-3 w-full">
          <div className="text-[10px] text-center uppercase tracking-widest text-white/40 mb-1">or</div>
          <PayPalButtons
            style={{ layout: "horizontal", color: "gold", shape: "pill", height: 40, tagline: false, label: "paypal" }}
            forceReRender={[bundle.lookup_key, currency]}
            createOrder={async () => {
              try {
                const r = await onPayPal.create(bundle.lookup_key);
                return r.order_id;
              } catch (e) {
                toast.error("Could not start PayPal");
                throw e;
              }
            }}
            onApprove={async (data) => {
              try {
                const r = await onPayPal.capture(data.orderID);
                if (r.credited) {
                  toast.success(`+${r.coins} coins`);
                } else {
                  toast.error("Payment not completed");
                }
              } catch (e) {
                toast.error("Capture failed");
              }
            }}
            onError={() => toast.error("PayPal error")}
            onCancel={() => toast.info("PayPal cancelled")}
          />
        </div>
      )}
    </motion.div>
  );
}

export default function Store() {
  const { user, setUser } = useAuth();
  const [tab, setTab] = useState("uno");
  const [data, setData] = useState({ uno_skins: [], skipbo_skins: [], backgrounds: [], coins: 0 });
  const [bundles, setBundles] = useState([]);
  const [paypalCfg, setPaypalCfg] = useState({ enabled: false, client_id: null, currency: "USD" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b, pp] = await Promise.all([
        api.get("/store/skins", { headers: authHeader() }),
        api.get("/coins/bundles"),
        api.get("/paypal/config"),
      ]);
      setData(s.data);
      setBundles(b.data.bundles);
      setPaypalCfg(pp.data);
    } catch (e) {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const buy = async (id) => {
    try {
      const r = await api.post(`/store/skins/${id}/buy`, {}, { headers: authHeader() });
      setUser(r.data.user);
      toast.success("Skin unlocked!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
    }
  };
  const equip = async (id) => {
    try {
      const r = await api.post(`/store/skins/${id}/equip`, {}, { headers: authHeader() });
      setUser(r.data.user);
      toast.success("Equipped");
      load();
    } catch (e) { toast.error("Could not equip"); }
  };
  const buyBundle = async (lookup_key) => {
    try {
      const r = await api.post("/payments/checkout",
        { lookup_key, origin_url: window.location.origin },
        { headers: authHeader() });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  const paypalActions = {
    create: async (lookup_key) => {
      const r = await api.post("/paypal/orders/create", { lookup_key }, { headers: authHeader() });
      return r.data;
    },
    capture: async (order_id) => {
      const r = await api.post(`/paypal/orders/${order_id}/capture`, {}, { headers: authHeader() });
      // Refresh user coins locally
      try {
        const me = await api.get("/auth/me", { headers: authHeader() });
        setUser(me.data);
      } catch (e) {}
      return r.data;
    },
  };

  const tabCls = (id) => `px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-semibold transition-colors ${tab === id ? "bg-white text-black" : "text-white/70 hover:text-white bg-white/5"}`;

  return (
    <div className="min-h-screen" data-testid={STORE.container}>
      <TopNav/>
      <main className="max-w-6xl mx-auto px-4 sm:px-10 py-6 sm:py-10 relative z-10">
        <div className="mb-6 sm:mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Store</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">Dress your deck.</h1>
          </div>
          <div data-testid={STORE.coinsBadge} className="glass px-4 py-2 rounded-full flex items-center gap-2">
            <span className="text-lg">🪙</span>
            <span className="font-display text-xl" style={{color:"var(--uno-yellow)"}}>{data.coins}</span>
            <span className="text-xs uppercase tracking-[0.2em] text-white/50 ml-1">coins</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button data-testid={STORE.tabUno} onClick={() => setTab("uno")} className={tabCls("uno")}>Uno skins</button>
          <button data-testid={STORE.tabSkipbo} onClick={() => setTab("skipbo")} className={tabCls("skipbo")}>Skip-Bo skins</button>
          <button data-testid="store-tab-bg" onClick={() => setTab("bg")} className={tabCls("bg")}>Backgrounds</button>
          <button data-testid={STORE.tabCoins} onClick={() => setTab("coins")} className={tabCls("coins")}>Buy coins</button>
        </div>

        {loading && <div className="text-white/50">Loading…</div>}

        {tab === "uno" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {data.uno_skins.map(skin => (
              <SkinCard key={skin.id} skin={skin} onBuy={buy} onEquip={equip} coins={data.coins} category="uno"/>
            ))}
          </div>
        )}
        {tab === "skipbo" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {data.skipbo_skins.map(skin => (
              <SkinCard key={skin.id} skin={skin} onBuy={buy} onEquip={equip} coins={data.coins} category="skipbo"/>
            ))}
          </div>
        )}
        {tab === "bg" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {(data.backgrounds || []).map(skin => (
              <SkinCard key={skin.id} skin={skin} onBuy={buy} onEquip={equip} coins={data.coins} category="background"/>
            ))}
          </div>
        )}
        {tab === "coins" && (
          <div>
            <div className="text-white/60 text-sm mb-5">Buy coins to unlock skins and other cosmetics. Card via Stripe · Wallet via PayPal.</div>
            <PayPalScriptProvider
              deferLoading={!paypalCfg.enabled}
              options={{
                clientId: paypalCfg.client_id || "test",
                currency: paypalCfg.currency || "USD",
                intent: "capture",
                components: "buttons",
              }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
                {bundles.map(b => (
                  <CoinBundle
                    key={b.lookup_key}
                    bundle={b}
                    onBuy={buyBundle}
                    onPayPal={paypalActions}
                    paypalReady={paypalCfg.enabled && !!paypalCfg.client_id}
                    currency={paypalCfg.currency}
                  />
                ))}
              </div>
            </PayPalScriptProvider>
            <div className="mt-6 text-xs text-white/40">
              Card test: 4242 4242 4242 4242 · any future expiry · any CVC. PayPal uses your real account ({paypalCfg.mode === "live" ? "live" : "sandbox"} mode).
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
